import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { genericEntityConstants } from "../../../api/GenericConstants";
import { ISignableEntity } from "../../../api/GenericTypes";
import { Button } from "../../../common/buttons/Button/Button";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { AlertModal, AlertModalOnProceedProps } from "../../../common/modals/AlertModal/AlertModal";
import { useEntityApi } from "../../../api/useEntityApi";
import { useQueryClient } from "@tanstack/react-query";
import { useSigninRedirectWithAction } from "../../../main/LoginPage/OidcRedirect";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { getDetailLink } from "../../../main/Routing";
import { TextareaFormField } from "../../../common/formfields/TextareaFormField";
import { useForm } from "react-hook-form";
import styles from "../../../common/modals/AlertModal/AlertModal.module.css";
import { SelectFormField } from "../../../common/formfields/SelectFormField";
import { Signature, SignatureTypes } from "../../../api/Signature";
import { ToggleButtonComponent } from "../../../ViewerUIElements/ToggleButtonComponent";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";

export interface GenericEntitySignButtonProps<Entity = GenericEntity> {
  btnClass?: string;
  hideLabel?: boolean;
  onSuccess?: (data: Entity) => void;
  overrideRedirectUrl?: (entity: Entity) => string;
  onClickCallback?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
}

export const GenericEntitySignButton = <
  Entity extends GenericEntity & Partial<ISignableEntity>,
  ComponentProps extends GenericEntitySignButtonProps<Entity>,
>({
  entityTypeId,
  entity,
  componentProps,
}: GenericEntityComponentProps<Entity, ComponentProps>) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { entityConstants } = genericEntityConstants[entityTypeId];
  const { signMutationAsync, isLoadingSignMutation } = useEntityApi<Entity>(entityConstants.resource);

  const queryClient = useQueryClient();

  const { route, session } = useContext(SessionContext);

  const onSuccessCallback = useCallback(
    (data: Entity) => {
      componentProps?.onSuccess?.(data);
      queryClient.invalidateQueries({ queryKey: [entityConstants.resource] });
    },
    [componentProps, entityConstants.resource, queryClient]
  );

  // sso session
  const isSSOSession = session?.type === "jwt";
  const { handleReauth } = useSigninRedirectWithAction();

  // additional elements for the signature item
  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<
    Partial<{
      signatureType: {
        id: Signature["signatureType"];
        label: Signature["signatureType"];
        value: Signature["signatureType"];
      };
      comment: string;
    }>
  >({});
  const currentSignatureType = watch("signatureType");
  const currentComment = watch("comment");

  const handleSign = useCallback(
    async (e, props?: AlertModalOnProceedProps) => {
      if (!entity?.id) return;
      if (!!isSSOSession) {
        handleReauth({
          actions: ["Sign"],
          entityIntIds: [entity.id],
          entityType: entityTypeId,
          redirectUrl:
            componentProps?.overrideRedirectUrl?.(entity) ??
            route(getDetailLink(entityConstants.frontendIndexRoute, entity.id)),
          signature: { comment: currentComment, signatureType: currentSignatureType?.value },
        });
      } else {
        await signMutationAsync(
          {
            id: entity.id,
            body: {
              credentials: props?.credentials,
              signature: { signatureType: currentSignatureType?.value, comment: currentComment },
            },
            showToast: true,
            entityName: entityConstants?.entitySingular,
          },
          { onSuccess: onSuccessCallback }
        ).catch((e) => console.error(e));
      }
      setShowModal(false);
    },
    [
      componentProps,
      currentComment,
      currentSignatureType?.value,
      entity,
      entityConstants?.entitySingular,
      entityConstants.frontendIndexRoute,
      entityTypeId,
      handleReauth,
      isSSOSession,
      onSuccessCallback,
      route,
      signMutationAsync,
    ]
  );

  const signatureTypes = useMemo(() => {
    var hasReviewer = !!entity?.signatures?.find((s) => s.signatureType === "Reviewer");
    return SignatureTypes.filter((t) => t !== "Unknown")
      .filter((t) => !!entity?.signatures?.length || t !== "Reviewer")
      .filter((t) => !hasReviewer || t !== "Contributor")
      .map((t) => ({
        id: t,
        label: t,
        value: t,
      }));
  }, [entity?.signatures]);

  const [approved, setApproved] = useState<boolean>(false);

  useEffect(() => {
    if (!showModal) {
      reset();
      setApproved(false);
    }
    if (!!entity?.signatures && !entity?.signatures?.length)
      setValue("signatureType", { id: "Contributor", label: "Contributor", value: "Contributor" });
    var hasReviewer = !!entity?.signatures?.find((s) => s.signatureType === "Reviewer");
    if (hasReviewer) {
      setValue("signatureType", { id: "Reviewer", label: "Reviewer", value: "Reviewer" });
    }
  }, [entity?.signatures, entity?.signatures?.length, reset, setValue, showModal]);

  const { canSign } = useEntityPermissions({ entityTypeId });

  if (!entity || !entityConstants) return null;
  return (
    <>
      <Button
        onClick={(e) => {
          setShowModal(true);
          componentProps?.onClickCallback?.(e);
        }}
        loading={isLoadingSignMutation}
        disabled={!canSign(entity)}
        className={componentProps?.btnClass ?? `btn btn-block btn-sm btn-ghost-primary flex align-center gap-5`}
      >
        <LucideIcon name="signature" />
        {!componentProps?.hideLabel && "Add signature"}
      </Button>

      {showModal && (
        <AlertModal
          showModal={showModal}
          setShowModal={setShowModal}
          type="primary"
          onProceed={handleSign}
          onCancel={() => setShowModal(false)}
          title="Add signature"
          description="Are you sure you want to sign this content? This will prevent other users from editing this content."
          forceUserAuthentication={!isSSOSession}
          loading={isLoadingSignMutation}
          children={
            <form
              className={`form-horizontal ${styles.form_holder}`}
              style={{
                margin: 0,
                marginBottom: "5px",
                padding: "0 10px",
                border: "none",
                boxShadow: "none",
                textAlign: "left",
              }}
            >
              <SelectFormField
                id="signatureType"
                label="Signature type"
                items={signatureTypes}
                isDisabled={signatureTypes.length === 1}
                getOptionLabel={(item) => item.label}
                getOptionValue={(item) => item.value}
                errors={errors}
                control={control}
                required
              />

              <TextareaFormField
                id="comment"
                label="Comment"
                errors={errors}
                register={register}
                horizontal
                placeholder="Enter comment..."
                autoFocus={!!isSSOSession}
              />

              {!!currentSignatureType?.value && (
                <>
                  <hr style={{ margin: "10px 0", borderColor: "var(--gray-300)" }} />
                  <label
                    className="flex align-center gap-5"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setApproved((prevState) => !prevState);
                    }}
                  >
                    <div className="col-sm-3 flex align-center justify-center">
                      <ToggleButtonComponent checked={approved}>{null}</ToggleButtonComponent>
                      <span className={styles.required} style={{ color: "var(--danger)" }}>
                        *
                      </span>
                    </div>
                    <h5 style={{ padding: 0, margin: 0, paddingLeft: "10px" }}>
                      {currentSignatureType.value === "Contributor" &&
                        "I confirm that I have contributed to this content and that the content is accurate."}
                      {currentSignatureType.value === "Reviewer" &&
                        "I confirm that I have reviewed this content. All contributors are acknowledged and the content meets the required standards."}
                    </h5>
                  </label>
                  <hr style={{ margin: "10px 0", borderColor: "var(--gray-300)" }} />
                </>
              )}
            </form>
          }
          proceedLabel="Add signature"
          iconOverride="signature"
          proceedDisabled={!currentSignatureType?.value || !approved}
        />
      )}
    </>
  );
};
